import React from "react";
import { Mail, Shield, ExternalLink, Lock, Sparkles } from "lucide-react";
import { useUnlock } from "@/pages/Dashboard";
import { usePrice } from "@/lib/price";

const WHOP_FALLBACK = "https://whop.com/checkout/plan_hfsvR7lsOjQds";

function Field({ label, value, mono = true }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 py-3 border-b-2 border-dashed border-[#0a0a0a]/15">
      <div className="font-accent text-[10px] text-[#0a0a0a]/55 sm:w-32 shrink-0 tracking-widest">
        {label}
      </div>
      <div className={`${mono ? "font-mono-body" : "font-display"} text-sm break-all`}>
        {value || <span className="text-[#0a0a0a]/40">—</span>}
      </div>
    </div>
  );
}

export default function Profile() {
  const { me } = useUnlock();
  const u = me?.user || {};
  const paid = Boolean(me?.paid);
  const { price, checkout } = usePrice();
  const whop = checkout || WHOP_FALLBACK;

  return (
    <div data-testid="dash-profile" className="space-y-6 max-w-3xl">
      <header>
        <div className="font-accent text-xs bg-[#0a0a0a] text-[#FFC400] inline-block px-2.5 py-1 mb-3">
          PROFILE
        </div>
        <h1 className="font-display text-3xl md:text-4xl leading-[0.95]">
          The bits we know{" "}
          <span className="bg-[#FFC400] px-2">about you.</span>
        </h1>
      </header>

      <section
        data-testid="profile-card"
        className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-6 md:p-8"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-[#FFC400] border-2 border-[#0a0a0a] flex items-center justify-center">
            <Mail size={20} strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-xl truncate">{u.email || me?.email || "—"}</div>
            <div className="font-mono-body text-xs text-[#0a0a0a]/55">
              {paid ? "All-Access · lifetime" : "Free account"}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Field label="EMAIL" value={u.email || me?.email} />
          <Field label="MEMBER SINCE" value={u.created_at || u.joined || ""} />
          <Field label="WHOP USER ID" value={u.whop_id || u.id || ""} />
          <Field label="ENTITLEMENT" value={paid ? "ALL-ACCESS" : "FREE"} mono={false} />
        </div>

        {!paid && (
          <a
            href={whop}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="profile-upgrade"
            className="hover-pop mt-7 inline-flex items-center gap-2 bg-[#FFC400] text-[#0a0a0a] border-4 border-[#0a0a0a] px-5 py-3 font-accent text-sm shadow-brutal-sm"
          >
            <Sparkles size={14} strokeWidth={3} /> Unlock All-Access — {price}
            <ExternalLink size={11} strokeWidth={3} />
          </a>
        )}
      </section>

      <section className="bg-[#FDFBF7] border-4 border-[#0a0a0a] p-6">
        <div className="flex items-start gap-3">
          <Shield size={18} strokeWidth={2.5} className="text-[#E10600] mt-0.5" />
          <div>
            <div className="font-display text-lg">Your data is yours.</div>
            <p className="font-mono-body text-sm text-[#0a0a0a]/75 mt-1.5 leading-relaxed">
              We store an email + an auth cookie. Nothing else. Want it gone? Email{" "}
              <a
                href="mailto:support@jobhacki.com"
                className="font-bold border-b-2 border-[#0a0a0a]"
              >
                support@jobhacki.com
              </a>{" "}
              and we delete it. See{" "}
              <a href="/privacy" className="font-bold border-b-2 border-[#0a0a0a]">
                Privacy
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      {!paid && (
        <section className="bg-[#0a0a0a] text-[#FDFBF7] border-4 border-[#0a0a0a] p-6">
          <div className="flex items-start gap-3">
            <Lock size={18} strokeWidth={2.5} className="text-[#FFC400] mt-0.5" />
            <div>
              <div className="font-display text-lg">Locked tools on your account</div>
              <p className="font-mono-body text-sm text-white/75 mt-1.5">
                Resume Builder · Readiness Simulator · OmniCut · OmniScript. One-time {price} unlocks them all forever.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
